import { randomUUID } from "./crypto";
import { TimeEntryType, TimeSettings } from "./model";
import { applyAutoBreaks, applyAutoMeals } from "./time";
import { toDateString } from "./utils/date.util";
import { parseTimes, toTimeString } from "./utils/time.util";
/**
 * New entries on past dates are actuals, not plans: when `date` lies before `today` and no final
 * times exist yet, the planned times move to `startFinal`/`endFinal` and the planned fields are
 * cleared. All other fields (including `breakPlanned`) are left untouched. Unassigned entries
 * (`employeeId === null`, deliberately strict) are never flipped — the flip happens on assignment
 * instead (see {@link resetAssignmentState}).
 */
export function applyPastDateFlip(date, times, today = toDateString(new Date())) {
    if (times.employeeId === null) {
        return times;
    }
    if (date < today && !times.startFinal && !times.endFinal) {
        times.startFinal = times.startPlanned;
        times.endFinal = times.endPlanned;
        times.startPlanned = null;
        times.endPlanned = null;
    }
    return times;
}
const sameTime = (a, b) => (a?.getTime() ?? null) === (b?.getTime() ?? null);
/**
 * Finals that exist only because a rule set stamped the planned times into them (tracking
 * disabled): no logged times, finals equal to planned. Punch-backed, manually entered, and
 * past-date-flipped finals (planned cleared) don't qualify.
 */
export function hasComputedFinals(entry) {
    return (!!(entry.startFinal || entry.endFinal) &&
        !entry.startLogged &&
        !entry.endLogged &&
        sameTime(entry.startFinal, entry.startPlanned) &&
        sameTime(entry.endFinal, entry.endPlanned));
}
/**
 * Clears computed finals (see {@link hasComputedFinals}) along with the break derived from them;
 * real records survive.
 */
export function wipeComputedFinals(entry) {
    if (!hasComputedFinals(entry)) {
        return false;
    }
    entry.startFinal = null;
    entry.endFinal = null;
    entry.break = null;
    entry.breakAuto = null;
    return true;
}
/**
 * Stamps entries for writing: mints ids for new entries, bumps `updated`, and applies the
 * auto-publish rule — an entry becomes employee-visible immediately when publishing is requested
 * explicitly, when its type is always visible (absences and adjustments), or when it is
 * past-dated. Future Work/CompDay/Free entries stay unpublished drafts until the roster is
 * published separately.
 */
export function prepareTimeEntriesForWrite(entries, options = {}) {
    const { publish = false, setUpdated = true, uuid = randomUUID } = options;
    for (const e of entries) {
        if (setUpdated) {
            e.updated = new Date();
        }
        if (!e.id) {
            e.id = uuid();
        }
        if (shouldAutoPublish(e, publish)) {
            e.setPublished();
        }
    }
    return entries;
}
function shouldAutoPublish(entry, publish) {
    return (publish || ![TimeEntryType.Work, TimeEntryType.CompDay, TimeEntryType.Free].includes(entry.type) || entry.isPast);
}
/**
 * Decides how a removal must be persisted: "hard" (row removal) is only safe for never-published
 * entries without recorded work; anything else keeps a soft-deleted tombstone so logged times
 * survive. Pure — apply the actual deletion stamps with {@link markRemoved}.
 */
export function classifyRemoval(entry) {
    const hasLoggedTimes = !!(entry.startLogged || entry.endLogged || entry.startFinal || entry.endFinal);
    return !entry.published && !hasLoggedTimes ? "hard" : "soft";
}
/**
 * Stamps an entry as deleted (`deleted`/`updated`) and, for published entries, re-stamps the
 * publish snapshot under the auto-publish rule so the employee sees the removal — legacy
 * unpublished tombstones stay unpublished.
 */
export function markRemoved(entry, options = {}) {
    const { publish = false } = options;
    const date = new Date();
    entry.deleted = date;
    entry.updated = date;
    if (entry.published && shouldAutoPublish(entry, publish)) {
        entry.setPublished();
    }
}
/**
 * Moves the wall-clock times of `source` onto a new `date`, keeping the times of day and the
 * overnight roll (end time before start time ⇒ end lands on the following day). Returns a new
 * object; `source` is not modified.
 */
export function moveTimesToDate(source, date) {
    const [startPlanned, endPlanned] = parseTimes(date, toTimeString(source.startPlanned), toTimeString(source.endPlanned));
    const [startFinal, endFinal] = parseTimes(date, toTimeString(source.startFinal), toTimeString(source.endFinal));
    return { startPlanned, endPlanned, startFinal, endFinal };
}
/**
 * Normalizes an entry before it is written: backfills `positionId` from the position relation,
 * mirrors planned⇄final times for departments with time tracking disabled, and — once final times
 * exist — clears `startBreak`, applies auto breaks, and (opt-in) auto meals deduplicated against
 * the employee's other entries of the day.
 *
 * `company` may be absent pre-login; entries with final times then throw, matching the historical
 * App behavior — without a company the right break and meal settings cannot be resolved.
 */
export function normalizeTimeEntry(entry, company, options = {}) {
    const { applyAutoMeals: autoMeals = false, otherEntries = [] } = options;
    backfillPositionId(entry);
    const timeSettings = company?.getTimeSettings({ timeEntry: entry }) ?? new TimeSettings();
    // unassigned entries never compute finals — heal stale mirrored ones instead of re-mirroring
    if (entry.employeeId == null) {
        wipeComputedFinals(entry);
    }
    else {
        mirrorTimesWhenTrackingDisabled(entry, timeSettings);
    }
    if (!entry.final) {
        return;
    }
    if (!company) {
        throw new Error("Cannot normalize a time entry with final times without a company");
    }
    applyBreaksToFinishedEntry(entry, timeSettings);
    if (autoMeals) {
        applyAutoMeals(company, entry, availableMealsFromEntries(entry, otherEntries));
    }
}
/**
 * Moves an entry to another employee (or to unassigned), applying the side effects the new
 * assignment requires (see {@link resetAssignmentState}). Use this instead of writing `employeeId`
 * directly; normalization stays with the write path. A no-op when the assignment doesn't change.
 */
export function assignEmployee(entry, employeeId) {
    if (entry.employeeId === employeeId) {
        return;
    }
    entry.employeeId = employeeId;
    resetAssignmentState(entry);
}
/**
 * Resets the rule-set-derived state an entry's previous assignment produced — the new `employeeId`
 * is already set: computed finals from the previous rule set are wiped, an assigned past-dated
 * entry gets the planned→final flip, an unassigned entry gets a previous flip reverted.
 * Punch-backed and manually entered finals survive untouched.
 *
 * Only live Work entries carry that state; everything else is left alone, same as on the server.
 */
export function resetAssignmentState(entry) {
    if (entry.type !== TimeEntryType.Work || entry.deleted) {
        return;
    }
    wipeComputedFinals(entry);
    if (entry.employeeId != null) {
        applyPastDateFlip(entry.date, entry);
    }
    else {
        revertPastDateFlip(entry);
    }
}
/**
 * Inverse of {@link applyPastDateFlip} for entries parked in the unassigned area: without punches,
 * an open entry cannot assert worked times, so flip-shaped finals (no logged and no planned times)
 * move back into the planned fields and the derived break is cleared. Lossless — the times survive
 * as the plan.
 */
function revertPastDateFlip(entry) {
    const flipShaped = (entry.startFinal || entry.endFinal) &&
        !entry.startLogged &&
        !entry.endLogged &&
        !entry.startPlanned &&
        !entry.endPlanned;
    if (!flipShaped) {
        return;
    }
    entry.startPlanned = entry.startFinal;
    entry.endPlanned = entry.endFinal;
    entry.startFinal = null;
    entry.endFinal = null;
    entry.break = null;
    entry.breakAuto = null;
}
function backfillPositionId(entry) {
    if (entry.position && !entry.positionId) {
        entry.positionId = entry.position.id;
    }
}
/**
 * Departments without time tracking keep planned and final times equal (final times win when both
 * exist).
 */
function mirrorTimesWhenTrackingDisabled(entry, { trackingEnabled }) {
    if (trackingEnabled) {
        return;
    }
    const start = entry.startFinal || entry.startPlanned;
    const end = entry.endFinal || entry.endPlanned;
    entry.startPlanned = start;
    entry.startFinal = start;
    entry.endPlanned = end;
    entry.endFinal = end;
}
/** A finished shift gets its break derived from the resolved time settings; an open break is closed. */
function applyBreaksToFinishedEntry(entry, { breakMode, autoBreaks }) {
    entry.startBreak = null;
    applyAutoBreaks(entry, breakMode, autoBreaks);
}
/**
 * Which meals the entry's employee can still receive that day: every meal already booked on
 * another live entry of the same employee and date is taken. In-memory twin of the repository's
 * meals-availability query.
 */
function availableMealsFromEntries(entry, otherEntries) {
    return otherEntries
        .filter((e) => !e.deleted && e.employeeId === entry.employeeId && e.date === entry.date)
        .reduce((total, e) => ({
        breakfast: total.breakfast && !e.mealsBreakfast,
        lunch: total.lunch && !e.mealsLunch,
        dinner: total.dinner && !e.mealsDinner,
    }), {
        breakfast: true,
        lunch: true,
        dinner: true,
    });
}
//# sourceMappingURL=time-entry-write.js.map